// src/pages/Browser/components/BrowserNavBar.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  FaChevronLeft, 
  FaChevronRight, 
  FaHome, 
  FaRedo, 
  FaLayerGroup,
  FaPlus,
  FaEllipsisH,
  FaStar,
  FaTimesCircle,
  FaTimes,
  FaQuestionCircle
} from 'react-icons/fa';
import HowToConnectModal from './HowToConnectModal';
import './BrowserNavBar.css';

const BrowserNavBar = ({
  canGoBack,
  canGoForward,
  onGoBack,
  onGoForward,
  onRefresh,
  onHome,
  isShowingHome,
  tabs,
  activeTabIndex,
  onTabSelect,
  onTabClose,
  onNewTab,
  onAddFavorite
}) => {
  const { t } = useTranslation();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showTabsModal, setShowTabsModal] = useState(false);
  const [showHowToConnectModal, setShowHowToConnectModal] = useState(false);

  const handleMoreClick = () => {
    setShowMoreMenu(!showMoreMenu);
  };

  const handleTabsClick = () => {
    setShowTabsModal(true);
    setShowMoreMenu(false);
  };

  const handleCloseTabsModal = () => {
    setShowTabsModal(false);
  };

  const handleAddFavorite = () => {
    onAddFavorite();
    setShowMoreMenu(false);
  };

  const handleNewTab = () => {
    onNewTab();
    setShowMoreMenu(false);
    setShowTabsModal(false);
  };

  const handleShowHowToConnect = () => {
    setShowHowToConnectModal(true);
    setShowMoreMenu(false);
  };

  const handleCloseHowToConnectModal = () => {
    setShowHowToConnectModal(false);
  };

  const handleCloseAllTabs = () => {
    tabs.forEach((_, index) => {
      if (index !== activeTabIndex) {
        onTabClose(index);
      }
    });
    setShowTabsModal(false);
  };

  return (
    <>
      <div className="browser-navbar">
        <button 
          className="nav-button" 
          onClick={onGoBack}
          disabled={!canGoBack}
          aria-label={t('browser.back')}
        >
          <div className="nav-button-icon">
            <FaChevronLeft />
          </div>
        </button>
        
        <button 
          className="nav-button" 
          onClick={onGoForward}
          disabled={!canGoForward}
          aria-label={t('browser.forward')}
        >
          <div className="nav-button-icon">
            <FaChevronRight />
          </div>
        </button>
        
        <button 
          className={`nav-button ${isShowingHome ? 'active' : ''}`}
          onClick={onHome}
          aria-label={t('browser.home')}
        >
          <div className="nav-button-icon">
            <FaHome />
          </div>
          {isShowingHome && <div className="nav-indicator"></div>}
        </button>
        
        <button 
          className="nav-button bottom-tabs-button"
          onClick={handleTabsClick}
          aria-label={t('browser.tabs')}
        >
          <div className="nav-button-icon">
            <FaLayerGroup />
          </div>
          {tabs && tabs.length > 1 && (
            <span className="tab-count">{tabs.length}</span>
          )}
        </button>
        
        <button 
          className="nav-button" 
          onClick={onRefresh}
          aria-label={t('browser.refresh')}
        >
          <div className="nav-button-icon">
            <FaRedo />
          </div>
        </button>
        
        <button
          className="nav-button"
          onClick={handleMoreClick}
          aria-label={t('browser.more')}
        >
          <div className="nav-button-icon">
            <FaEllipsisH />
          </div>
        </button>
      </div>

      {/* More menu popup */}
      {showMoreMenu && (
        <div className="more-menu">
          <div className="more-menu-item" onClick={handleNewTab}>
            <FaPlus className="more-menu-icon" />
            <span className="more-menu-text">{t('browser.newTab')}</span>
          </div>
          <div className="more-menu-item" onClick={handleAddFavorite}>
            <FaStar className="more-menu-icon" />
            <span className="more-menu-text">{t('browser.addToFavorites')}</span>
          </div>
          <div className="more-menu-item" onClick={handleShowHowToConnect}>
            <FaQuestionCircle className="more-menu-icon" />
            <span className="more-menu-text">{t('browser.howToConnect.menuItem', 'How to Connect?')}</span>
          </div>
        </div>
      )}

      {/* Tabs modal */}
      {showTabsModal && (
        <div className="tabs-modal">
          <div className="tabs-header">
            <div className="tabs-title">{t('browser.tabs')}</div>
            <div className="tabs-actions">
              <button 
                className="tabs-action-button"
                onClick={handleCloseAllTabs}
              >
                {t('browser.closeAllTabs')}
              </button>
              <button 
                className="tabs-action-button"
                onClick={handleCloseTabsModal}
              >
                {t('browser.done')}
              </button>
            </div>
          </div>
          
          <div className="tabs-list">
            {tabs && tabs.map((tab, index) => (
              <div 
                key={index} 
                className={`tab-item ${index === activeTabIndex ? 'active' : ''}`}
                onClick={() => {
                  onTabSelect(index);
                  handleCloseTabsModal();
                }}
              >
                <div className="tab-content">
                  <div className="tab-preview" />
                  <div className="tab-info">
                    <img 
                      src={tab.favicon || '/assets/wallet-logo.png'} 
                      alt="" 
                      className="tab-favicon"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/assets/wallet-logo.png';
                      }}
                    />
                    <div className="tab-url">{tab.url || t('browser.newTab')}</div>
                  </div>
                </div>
                {tabs.length > 1 && (
                  <button 
                    className="tab-close"
                    onClick={(e) => {
                      e.stopPropagation();
                      onTabClose(index);
                    }}
                    aria-label={t('browser.closeTab')}
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            ))}
          </div>
          
          <div className="tabs-bottom">
            <button className="tabs-new-button" onClick={handleNewTab}>
              <FaPlus />
              {t('browser.newTab')}
            </button>
          </div>
        </div>
      )}

      {/* How to Connect Modal */}
      <HowToConnectModal
        isOpen={showHowToConnectModal}
        onClose={handleCloseHowToConnectModal}
      />
    </>
  );
};

export default BrowserNavBar;